import { motion } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

const TRUCK_IMG =
  "https://images.pexels.com/photos/17720190/pexels-photo-17720190.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CHAPTERS = [
  {
    n: "01",
    title: "PLANNED FREIGHT COORDINATION",
    body: "We review shipment requirements, equipment needs, origin and destination, and timing before coordinating a load.",
  },
  {
    n: "02",
    title: "CARRIER QUALIFICATION",
    body: "Operating authority, insurance, safety information, and equipment are reviewed as part of carrier selection and onboarding.",
  },
  {
    n: "03",
    title: "CLEAR COMMUNICATION",
    body: "Shippers and carriers receive direct points of contact and practical shipment updates from pickup through delivery.",
  },
  {
    n: "04",
    title: "DISPATCH SUPPORT",
    body: "Our team stays involved throughout coordination and remains available for questions, updates, and shipment exceptions.",
  },
];

export default function About() {
  return (
    <section id="about" className="relative scroll-mt-20 py-28">
      <div className="mx-auto w-full max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#F59E0B]">
            01 // WHO WE ARE
          </p>
          <h2 className="mt-4 font-display text-2xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            How We Work
          </h2>
        </motion.div>

        <div className="mt-16 grid gap-16 lg:grid-cols-[1fr_2fr]">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: EASE }}
              className="max-w-sm text-base leading-7 text-slate-300 lg:text-[17px] lg:leading-8"
            >
              Four practical working principles guide how we support shippers,
              carriers, and each shipment we coordinate.
            </motion.p>
            <div className="mt-10 hidden space-y-4 lg:block">
              {CHAPTERS.map((c) => (
                <motion.div
                  key={c.n}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="flex items-center gap-4"
                >
                  <span className="font-mono text-xs font-bold text-[#FF5500]">
                    {c.n}
                  </span>
                  <span className="font-mono text-[11px] tracking-[0.2em] text-slate-500">
                    {c.title}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, ease: EASE }}
              className="group relative overflow-hidden border border-[#222736] [clip-path:polygon(0_0,100%_0,100%_calc(100%-36px),calc(100%-36px)_100%,0_100%)]"
            >
              <img
                src={TRUCK_IMG}
                alt="Heavy duty commercial freight trucks illuminated at night"
                className="h-64 w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.03] group-hover:grayscale-0 sm:h-80"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0E]/85 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 border border-[#383F56] bg-[#0A0B0E]/80 px-3 py-1.5 font-mono text-[10px] tracking-[0.25em] text-slate-300 backdrop-blur">
                FLEET OPS — NIGHT LINEHAUL
              </div>
            </motion.div>

            {CHAPTERS.map((c, i) => (
              <motion.article
                key={c.n}
                data-testid={`manifesto-chapter-${i + 1}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: 0.05 * i, ease: EASE }}
                className="group border border-[#222736] bg-[#12141C] p-8 transition-colors duration-500 hover:border-[#383F56] hover:bg-[#1A1D28]"
              >
                <div className="flex items-baseline gap-5">
                  <span className="font-mono text-3xl font-extrabold tracking-tighter text-[#FF5500]">
                    {c.n}
                  </span>
                  <h3 className="font-display text-xl font-bold uppercase tracking-normal text-white sm:text-2xl">
                    {c.title}
                  </h3>
                </div>
                <div className="mt-4 h-px w-full bg-gradient-to-r from-[#FF5500]/60 via-[#222736] to-transparent" />
                <p className="mt-4 text-base leading-7 text-slate-300 lg:text-[17px] lg:leading-8">
                  {c.body}
                </p>
              </motion.article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
